import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft, Github, Star, GitFork, Users, Code2, Sparkles } from 'lucide-react';
import LabNavigation from '@/components/LabNavigation';

interface Project {
  name: string;
  lines: number;
  language: string;
  category: string;
}

const featuredProjects: Project[] = [
  { name: "Wordle Clone", lines: 87, language: "JavaScript", category: "Games" },
  { name: "Face Detection", lines: 42, language: "Python", category: "ML/AI" },
  { name: "Password Generator", lines: 31, language: "Python", category: "Security" },
  { name: "QR Code Generator", lines: 28, language: "Python", category: "Utilities" },
  { name: "Sentiment Analyzer", lines: 56, language: "Python", category: "ML/AI" },
  { name: "URL Shortener", lines: 45, language: "JavaScript", category: "Web" },
  { name: "Port Scanner", lines: 38, language: "Python", category: "Security" },
  { name: "Morse Code Translator", lines: 52, language: "Python", category: "Utilities" },
  { name: "Currency Converter", lines: 67, language: "JavaScript", category: "Web" },
  { name: "File Organizer", lines: 73, language: "Python", category: "Productivity" },
  { name: "Huffman Compressor", lines: 94, language: "Python", category: "Algorithms" },
  { name: "OCR Tool", lines: 35, language: "Python", category: "ML/AI" },
];

const languageColors: Record<string, string> = {
  "Python": "#3572A5",
  "JavaScript": "#f1e05a",
  "Java": "#b07219",
  "C++": "#f34b7d",
  "Go": "#00ADD8",
};

const HundredLinesStudy = () => {
  const navigate = useNavigate();
  const [lineCount, setLineCount] = useState(100);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll();

  const counterOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0.3]);
  const counterScale = useTransform(scrollYProgress, [0, 0.2], [1, 0.6]);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 300);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      const newCount = Math.max(0, Math.floor(100 - (scrollPercent * 100)));
      setLineCount(newCount);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={containerRef} className="min-h-screen bg-[#0d1117] text-[#c9d1d9] font-mono">
      {/* Floating Line Counter */}
      <motion.div
        style={{ opacity: counterOpacity, scale: counterScale }}
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0"
      >
        <div className="text-[20vw] font-black text-[#161b22] leading-none select-none">
          {lineCount.toString().padStart(3, '0')}
        </div>
      </motion.div>

      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/')}
        className="fixed top-8 left-8 z-50 flex items-center gap-2 text-[#8b949e] hover:text-[#c9d1d9] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back</span>
      </motion.button>

      {/* Hero */}
      <section className="min-h-screen flex flex-col justify-center items-center px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl"
        >
          {/* Code Comment Style Header */}
          <div className="text-[#8b949e] text-sm mb-8 text-left max-w-xl mx-auto">
            <p>/**</p>
            <p> * @project 100LinesOfCode</p>
            <p> * @philosophy Less is more. Constraints breed creativity.</p>
            <p> * @stars 717+ | @forks 332 | @projects 164+</p>
            <p> */</p>
          </div>

          <h1 className="text-5xl md:text-7xl font-black text-[#f0f6fc] mb-6 tracking-tight">
            <span className="text-[#f1e05a]">100</span> Lines.
            <br />
            <span className="text-[#8b949e]">Infinite</span> Possibilities.
          </h1>

          <p className="text-xl text-[#8b949e] mb-12 max-w-2xl mx-auto leading-relaxed">
            You don't need thousands of lines to build something meaningful.
            <br />
            <span className="text-[#58a6ff]">This is the proof.</span>
          </p>

          {/* Stats Bar */}
          <div className="flex flex-wrap justify-center gap-8 text-sm">
            {[
              { icon: Star, value: "717+", label: "Stars" },
              { icon: GitFork, value: "332", label: "Forks" },
              { icon: Code2, value: "164+", label: "Projects" },
              { icon: Users, value: "200+", label: "Contributors" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="flex items-center gap-2"
              >
                <stat.icon className="w-4 h-4 text-[#58a6ff]" />
                <span className="text-[#f0f6fc] font-bold">{stat.value}</span>
                <span className="text-[#8b949e]">{stat.label}</span>
              </motion.div>
            ))}
          </div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="mt-20 text-[#8b949e] text-xs"
          >
            <p>scroll to explore</p>
            <p className="animate-bounce mt-2">↓</p>
          </motion.div>
        </motion.div>
      </section>

      {/* The Philosophy */}
      <section className="py-32 px-8 relative z-10">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-[#f0f6fc] mb-12">
              <span className="text-[#f1e05a]">const</span> philosophy = {"{"}
            </h2>

            <div className="space-y-8 pl-8 border-l-2 border-[#30363d]">
              {[
                {
                  key: "constraint",
                  value: '"Limitation is the mother of invention"',
                  comment: "// Like haiku has 17 syllables, we have 100 lines"
                },
                {
                  key: "clarity",
                  value: '"If you can\'t explain it simply, you don\'t understand it"',
                  comment: "// Complex problems, simple solutions"
                },
                {
                  key: "accessibility",
                  value: '"Open source is not just code, it\'s education"',
                  comment: "// First-timers welcome. Always."
                },
                {
                  key: "proof",
                  value: '"Talk is cheap. Show me the code."',
                  comment: "// 164+ projects that actually work"
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className="group"
                >
                  <p className="text-[#8b949e] text-sm mb-1">{item.comment}</p>
                  <p className="text-lg">
                    <span className="text-[#ff7b72]">{item.key}</span>
                    <span className="text-[#8b949e]">:</span>{" "}
                    <span className="text-[#a5d6ff]">{item.value}</span>
                    <span className="text-[#8b949e]">,</span>
                  </p>
                </motion.div>
              ))}
            </div>

            <h2 className="text-3xl font-bold text-[#f0f6fc] mt-12">{"}"}</h2>
          </motion.div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="py-32 px-8 bg-[#161b22] relative z-10">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-[#f0f6fc] mb-4">
              Featured Projects
            </h2>
            <p className="text-[#8b949e]">
              Each one under 100 lines. Each one fully functional.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredProjects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="group bg-[#0d1117] border border-[#30363d] rounded-lg p-6 hover:border-[#58a6ff] transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-[#f0f6fc] font-semibold group-hover:text-[#58a6ff] transition-colors">
                      {project.name}
                    </h3>
                    <span className="text-xs text-[#8b949e]">{project.category}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-[#f0f6fc]">
                      {project.lines}
                    </div>
                    <div className="text-xs text-[#8b949e]">lines</div>
                  </div>
                </div>

                {/* Line visualization */}
                <div className="h-2 bg-[#21262d] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${project.lines}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.05 }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: languageColors[project.language] || '#8b949e' }}
                  />
                </div>

                <div className="flex items-center gap-2 mt-4">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: languageColors[project.language] || '#8b949e' }}
                  />
                  <span className="text-xs text-[#8b949e]">{project.language}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-[#8b949e] text-sm">
              + 152 more projects across Games, ML/AI, Web, Security, and more
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Impact */}
      <section className="py-32 px-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-[#f0f6fc] mb-4">
              Beyond the Code
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: "First-Timer Gateway",
                desc: "Labeled issues guide newcomers through their first open-source contribution. Many contributors made their first PR here.",
                icon: Sparkles,
              },
              {
                title: "Hacktoberfest Regular",
                desc: "Every October, developers worldwide discover that meaningful contributions don't require massive codebases.",
                icon: Github,
              },
              {
                title: "Educational Resource",
                desc: "Used by coding bootcamps and CS courses to show that complexity isn't a prerequisite for functionality.",
                icon: Code2,
              },
              {
                title: "Cross-Language Playground",
                desc: "Python, JavaScript, Java, C++, Go, Bash, and more. The constraint is the lines, not the language.",
                icon: Users,
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#161b22] border border-[#30363d] rounded-lg p-6"
              >
                <item.icon className="w-8 h-8 text-[#58a6ff] mb-4" />
                <h3 className="text-[#f0f6fc] font-semibold mb-2">{item.title}</h3>
                <p className="text-[#8b949e] text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Manifesto */}
      <section className="py-32 px-8 bg-[#161b22] relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="text-6xl mb-8">📜</div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#f0f6fc] mb-8 leading-tight">
              "The best code is no code at all.
              <br />
              <span className="text-[#8b949e]">The second best is code that fits in your head."</span>
            </h2>
            <p className="text-[#8b949e] text-lg">
              100LinesOfCode isn't about arbitrary limits.
              <br />
              It's about proving that elegance trumps complexity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-8 relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold text-[#f0f6fc] mb-8">
              Join the Movement
            </h2>

            <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-8 font-mono text-sm">
              <span className="text-[#8b949e]">$</span>{" "}
              <span className="text-[#f0f6fc]">git clone https://github.com/josharsh/100LinesOfCode</span>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://github.com/josharsh/100LinesOfCode"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#238636] text-white rounded-lg font-semibold hover:bg-[#2ea043] transition-colors"
              >
                <Github className="w-5 h-5" />
                View Repository
              </a>
              <a
                href="https://github.com/josharsh/100LinesOfCode/issues?q=is%3Aissue+is%3Aopen+label%3A%22good+first+issue%22"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#21262d] text-[#c9d1d9] rounded-lg font-semibold hover:bg-[#30363d] transition-colors border border-[#30363d]"
              >
                First Issue
              </a>
            </div>

            <p className="mt-12 text-[#8b949e] text-sm">
              Built by <a href="/" className="text-[#58a6ff] hover:underline">Harsh Joshi</a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lab Navigation */}
      <LabNavigation currentLabId="100-lines" variant="dark" />
    </div>
  );
};

export default HundredLinesStudy;
